import AppColors from '../constants/AppColors';
import {
  DownIcon,
  LeftIcon,
  RightIcon,
  SortIcon,
  UpIcon,
  LeftEnableIcon,
  RightEnableIcon,
} from '../constants/IconData';
import { FONT_FAMILY } from '../constants/Theme';

// ─── Default props bundle for CQTreeTable ─────────────────────────────────────
// Drop this spread directly onto <CQTreeTable {...defaultTreeTableProp} ... />
export const defaultTreeTableProp = {
  renderSortUpIcon,
  renderSortIcon,
  renderSortDownIcon,
  getCellProps,
  getHeaderCellProps,
  getRowProps,

  RightIcon: <RightIcon />,
  LeftIcon: <LeftIcon />,
};

// ─── Factory: use when you need a highlighted row ────────────────────────────
// Usage: <CQTreeTable {...createTreeTableProps(highlightedId)} ... />
export function createTreeTableProps(highlightedRowDetails = null) {
  if (Array.isArray(highlightedRowDetails)) {
      return {
      renderSortUpIcon,
      renderSortIcon,
      renderSortDownIcon,
      getCellProps,
      getHeaderCellProps,
      getRowProps: (row, options) =>
        getEnhancedRowPropsV2(row, options, highlightedRowDetails),

      RightIcon: <RightIcon />,
      LeftIcon: <LeftIcon />,
    };
  }
  return {
    renderSortUpIcon,
    renderSortIcon,
    renderSortDownIcon,
    getCellProps,
    getHeaderCellProps,
    getRowProps: (row, options) =>
      getEnhancedRowProps(row, options, highlightedRowDetails),

    RightIcon: <RightIcon />,
    LeftIcon: <LeftIcon />,
  };
}

// ─── Pagination props (identical shape — works for both tables) ───────────────
export function paginationProps(pagination) {
  const canGoBack = pagination?.currentPage !== 1;
  const canGoNext = pagination?.totalPage !== pagination.currentPage;

  return {
    total: pagination.totalCount,
    pageCount: pagination.totalPage,
    size: pagination.showSize,
    currentPage: pagination.currentPage,
    LeftIcon: canGoBack ? <LeftEnableIcon /> : <LeftIcon />,
    RightIcon: canGoNext ? <RightEnableIcon /> : <RightIcon />,
  };
}

// ─── Sort icons ───────────────────────────────────────────────────────────────
export function renderSortUpIcon() {
  return (
    <UpIcon
      style={{
        width: 10,
        height: 10,
        marginLeft: 1,
      }}
    />
  );
}

export function renderSortDownIcon() {
  return (
    <DownIcon
      style={{
        width: 10,
        height: 10,
        marginLeft: 1,
      }}
    />
  );
}

export function renderSortIcon() {
  return (
    <SortIcon
      style={{
        width: 15,
        height: 15,
        marginLeft: 1,
      }}
    />
  );
}

// ─── Header cell styling ──────────────────────────────────────────────────────
// Extend `startingHeaderId` / `tableHeader` with any tree-specific column ids.
export function getHeaderCellProps(header) {
  const startingHeaderId = ['assigned_auditor', 'name', 'id'];
  const tableHeader = [
    'name',
    'upload_date',
    'status',
    'specialties',
    'auditors',
    'qas',
    'providers',
    'account'
  ];

  return {
    textTransform: 'none',
    fontFamily: FONT_FAMILY.ptSans,
    fontWeight: 'bold',
    fontSize: '16px',
    color: 'black',
    width: tableHeader.includes(header.id) ? '105%' : '100%',
    justifyContent: startingHeaderId.includes(header.id)
      ? 'flex-start'
      : 'center',
  };
}

// ─── Row props ────────────────────────────────────────────────────────────────
// NOTE: CQTreeTable also passes `row.depth` — you can use it here if you want
// per-depth background colours instead of (or alongside) the built-in ones.
export function getRowProps(row) {
  const bgs = ['white', '#f2faf2', '#f6f9fd', '#F5F3FF'];
  const bg  = bgs[Math.min(row.depth, bgs.length - 1)];
  return {
    style: { backgroundColor: bg }
  };
}

// ─── Cell props ───────────────────────────────────────────────────────────────
const TOOLTIP_CELL_ID = [
  'specialties',
  'auditors',
  'qas',
  'providers',
  'state'
]
export function getCellProps(cell) {
  let style = {
      fontFamily: FONT_FAMILY.ptSans,
      color: AppColors.secondary,
      fontSize: '16px',
      fontWeight: 'normal',
      textAlign: 'center',
      padding: '6px 9px'
  }
  if (TOOLTIP_CELL_ID.includes(cell.column.id)) {
    style = {
      ...style,
      padding: '6px 5px'
    }
  }
  return {
    style: style
  };
}

// ─── Enhanced row props (highlighted row support) ────────────────────────────
export function getEnhancedRowProps(
  row,
  options,
  highlightedRowId = null
) {
  const baseProps = getRowProps(row);
  const isHighlighted = String(highlightedRowId) === String(row.original?.upload_id) || 
                        String(highlightedRowId) === String(row.original?.id);

  return {
    ...baseProps,
    style: {
      ...baseProps.style,
      backgroundColor: isHighlighted
        ? '#e6f0ff'
        : baseProps.style?.backgroundColor || 'inherit',
      transition: 'background-color 0.3s ease',
    },
  };
}

// v2

export function getEnhancedRowPropsV2(
  row,
  options,
  // first value is the id, followed by the path row ids
  highlightedRowDetails = null
) {
  const baseProps = getRowProps(row);

  const highlightedRowId = highlightedRowDetails[0];
  let isHighlighted = String(highlightedRowId) === String(row.original?.upload_id) || 
                        String(highlightedRowId) === String(row.original?.id);

  let curRow = row;
  let rowIds = [];
  while (true) {
    rowIds.push(curRow.index);
    if (curRow.depth === 0) {
      break;
    } 
    curRow = curRow.getParentRow();
  }

  rowIds.reverse();
  rowIds.pop();

  if (highlightedRowDetails.length - 1 != rowIds.length) {
    isHighlighted = false;
  } else {
    // check if the path are same
    for (let idx = 0; idx < rowIds.length; idx++) {
      if (rowIds[idx] !== highlightedRowDetails[idx + 1]) {
        isHighlighted = false;
        break;
      }
    }
  }

  return {
    ...baseProps,
    style: {
      ...baseProps.style,
      backgroundColor: isHighlighted
        ? '#e6f0ff'
        : baseProps.style?.backgroundColor || 'inherit',
      transition: 'background-color 0.3s ease',
    },
  };
}